import React from 'react'
import { Filter as FilterIcon } from 'lucide-react'
import type { Report, DateRange } from './types'

interface Props {
  report: Report
  dateRange: DateRange
  filters: Record<string, string>
  loading: boolean
  onDateRangeChange: (range: DateRange) => void
  onFilterChange: (key: string, value: string) => void
  onGenerate: () => void
}

export const ReportConfigPanel: React.FC<Props> = ({
  report,
  dateRange,
  filters,
  loading,
  onDateRangeChange,
  onFilterChange,
  onGenerate,
}) => (
  <div className="space-y-4">
    {report.requiresDateRange && (
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">Date Range</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">From</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={e => onDateRangeChange({ ...dateRange, start: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">To</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={e => onDateRangeChange({ ...dateRange, end: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>
    )}

    {report.requiresFilters && report.availableFilters && (
      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon size={15} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {report.availableFilters.map(key => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 capitalize">
                {key.replace(/_/g, ' ')}
              </label>
              <input
                type="text"
                value={filters[key] || ''}
                onChange={e => onFilterChange(key, e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          ))}
        </div>
      </div>
    )}

    <button
      onClick={onGenerate}
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          Generating…
        </>
      ) : (
        'Generate Report'
      )}
    </button>
  </div>
)
